/* Ảnh mẫu khách gửi cho khách mẫu (hình minh hoạ vẽ bằng SVG, có nhãn "Ảnh minh hoạ") — chỉ lưu trên máy */
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import { putMedia } from '../../Customer360/mediaDb';
import { isDemoId } from './demo';
import { MIN, type LeadLocal, type Mang } from './logic';
import { mediaKey, type LeadPhoto, type ProfileTag } from './photo';

/** Số ngẫu nhiên lặp lại được theo id khách để ảnh mỗi lần tạo giống nhau */
const seeded = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const SKINS = ['#f1cfb4', '#e8bf9f', '#dcae8c'];
const BGS = [['#dfe9ee', '#b9cdd6'], ['#ece4dc', '#cfc0b3'], ['#e3e6ec', '#c3c8d4']];
const HAIR = '#2b211c';

interface FaceOpt { female: boolean; skin: string; bg: string[]; spots?: 'nam' | 'mun'; hooded?: boolean; recede?: boolean }

/** Khuôn mặt + vai (chân dung, cận má, cận mắt dùng chung, chỉ khác khung cắt) */
function face(o: FaceOpt, rnd: () => number) {
  const hairBack = o.female
    ? `<path d="M150 300 C130 120 470 120 450 300 L470 520 C430 560 170 560 130 520 Z" fill="${HAIR}"/>`
    : '';
  const hairTop = o.recede
    ? `<path d="M168 290 C160 110 440 110 432 290 L418 250 C400 200 372 188 344 206 C326 176 274 176 256 206 C228 188 200 200 182 250 Z" fill="${HAIR}"/>`
    : o.female
      ? `<path d="M168 280 C160 110 440 110 432 280 C400 200 330 170 300 176 C250 186 200 220 168 280 Z" fill="${HAIR}"/>`
      : `<path d="M166 270 C156 116 444 116 434 270 C420 214 380 186 300 186 C220 186 180 214 166 270 Z" fill="${HAIR}"/>`;
  let spots = '';
  if (o.spots === 'nam') {
    [[228, 330], [372, 330]].forEach(([cx, cy]) => {
      for (let i = 0; i < 9; i++) {
        spots += `<ellipse cx="${(cx + (rnd() - 0.5) * 60).toFixed(0)}" cy="${(cy + (rnd() - 0.5) * 40).toFixed(0)}" rx="${(6 + rnd() * 12).toFixed(0)}" ry="${(5 + rnd() * 9).toFixed(0)}" fill="#8a5a3b" opacity="${(0.18 + rnd() * 0.2).toFixed(2)}"/>`;
      }
    });
  } else if (o.spots === 'mun') {
    for (let i = 0; i < 16; i++) {
      const side = i % 2 ? 1 : -1;
      const x = 300 + side * (50 + rnd() * 70);
      const y = 300 + rnd() * 90;
      spots += `<circle cx="${x.toFixed(0)}" cy="${y.toFixed(0)}" r="${(4 + rnd() * 4).toFixed(1)}" fill="#e7a3a0" opacity=".7"/><circle cx="${x.toFixed(0)}" cy="${y.toFixed(0)}" r="${(1.6 + rnd() * 1.6).toFixed(1)}" fill="#c0564f"/>`;
    }
  }
  const lid = o.hooded
    ? '<path d="M222 268 C240 256 268 256 284 270" stroke="#9c6f55" stroke-width="7" fill="none" stroke-linecap="round"/><path d="M316 270 C332 256 360 256 378 268" stroke="#9c6f55" stroke-width="7" fill="none" stroke-linecap="round"/>'
    : '<path d="M224 262 C242 250 266 250 282 262" stroke="#b88a6c" stroke-width="2" fill="none"/><path d="M318 262 C334 250 358 250 376 262" stroke="#b88a6c" stroke-width="2" fill="none"/>';
  return `
    <defs><linearGradient id="bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${o.bg[0]}"/><stop offset="1" stop-color="${o.bg[1]}"/></linearGradient></defs>
    <rect width="600" height="600" fill="url(#bg)"/>
    ${hairBack}
    <path d="M90 600 C100 500 200 470 300 470 C400 470 500 500 510 600 Z" fill="${o.female ? '#7a8fa6' : '#4f6476'}"/>
    <rect x="262" y="400" width="76" height="90" rx="30" fill="${o.skin}"/>
    <ellipse cx="166" cy="300" rx="16" ry="30" fill="${o.skin}"/><ellipse cx="434" cy="300" rx="16" ry="30" fill="${o.skin}"/>
    <ellipse cx="300" cy="290" rx="135" ry="168" fill="${o.skin}"/>
    ${spots}
    ${hairTop}
    <path d="M220 236 C240 224 268 224 286 232" stroke="#3a2a22" stroke-width="7" fill="none" stroke-linecap="round"/>
    <path d="M314 232 C332 224 360 224 380 236" stroke="#3a2a22" stroke-width="7" fill="none" stroke-linecap="round"/>
    <ellipse cx="253" cy="274" rx="24" ry="11" fill="#fff"/><circle cx="253" cy="275" r="9" fill="#3b2a20"/>
    <ellipse cx="347" cy="274" rx="24" ry="11" fill="#fff"/><circle cx="347" cy="275" r="9" fill="#3b2a20"/>
    ${lid}
    <path d="M300 286 C296 318 288 334 300 344 C306 346 312 344 316 340" stroke="#b88a6c" stroke-width="3" fill="none" stroke-linecap="round"/>
    <path d="M262 382 C282 396 318 396 338 382 C318 404 282 404 262 382 Z" fill="${o.female ? '#c46a6a' : '#b47c6c'}"/>`;
}

/** Đỉnh đầu nhìn từ trên: tóc thưa dần vào giữa */
function scalp(skin: string, bg: string[], rnd: () => number) {
  let d = '';
  for (let i = 0; i < 2600; i++) {
    const a = rnd() * Math.PI * 2;
    const rr = Math.sqrt(rnd());
    const keep = rr < 0.32 ? 0.06 : rr < 0.5 ? 0.3 : rr < 0.65 ? 0.7 : 0.97;
    if (rnd() > keep) continue;
    const x = 300 + Math.cos(a) * rr * 200;
    const y = 300 + Math.sin(a) * rr * 230;
    const da = a + (rnd() - 0.5) * 0.9;
    const len = 14 + rnd() * 12;
    d += `M${x.toFixed(0)} ${y.toFixed(0)}l${(Math.cos(da) * len).toFixed(0)} ${(Math.sin(da) * len).toFixed(0)}`;
  }
  return `
    <defs><radialGradient id="sk" cx=".5" cy=".45" r=".6"><stop offset="0" stop-color="${skin}"/><stop offset="1" stop-color="#c99b7c"/></radialGradient></defs>
    <rect width="600" height="600" fill="${bg[1]}"/>
    <ellipse cx="300" cy="300" rx="225" ry="255" fill="url(#sk)"/>
    <path d="${d}" stroke="${HAIR}" stroke-width="2.6" stroke-linecap="round" opacity=".92"/>`;
}

/** Bọc hình + khung cắt + nhãn minh hoạ → data URL */
const toUrl = (body: string, label: string, view = '0 0 600 600') => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600">
    <svg width="600" height="600" viewBox="${view}" preserveAspectRatio="xMidYMid slice">${body}</svg>
    <rect x="16" y="548" width="${36 + label.length * 9.4}" height="34" rx="17" fill="#000" opacity=".5"/>
    <text x="34" y="571" font-family="Segoe UI, Arial, sans-serif" font-size="17" fill="#fff">${label}</text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

interface Shot { tag: ProfileTag; note: string; url: string; ago: number }

/** Bộ ảnh theo mảng: khách gửi tình trạng hiện tại + một ảnh chân dung */
function shotsFor(mang: Mang | undefined, female: boolean, seed: number, note = ''): Shot[] {
  const rnd = seeded(seed);
  const skin = SKINS[seed % SKINS.length];
  const bg = BGS[seed % BGS.length];
  const base: FaceOpt = { female, skin, bg };
  const portrait: Shot = { tag: 'Chân dung', note: 'Ảnh chân dung khách gửi', url: toUrl(face(base, rnd), 'Ảnh minh hoạ · Chân dung'), ago: 1 };
  if (mang === 'Newhair') {
    return [
      { tag: 'Tình trạng hiện tại', note: 'Đỉnh đầu, chụp dưới đèn', url: toUrl(scalp(skin, bg, rnd), 'Ảnh minh hoạ · Đỉnh đầu'), ago: 6 },
      { tag: 'Tình trạng hiện tại', note: 'Đường chân tóc phía trước', url: toUrl(face({ ...base, female: false, recede: true }, rnd), 'Ảnh minh hoạ · Chân tóc', '120 90 360 360'), ago: 5 },
      portrait,
    ];
  }
  if (mang === 'Clinic') {
    // Theo ghi chú trực page: hỏi mụn → ảnh mụn, còn lại ảnh nám
    const spots = /mụn/i.test(note) ? 'mun' : 'nam';
    return [
      { tag: 'Tình trạng hiện tại', note: spots === 'nam' ? 'Nám hai bên má, không trang điểm' : 'Mụn ẩn và thâm vùng má', url: toUrl(face({ ...base, spots }, rnd), 'Ảnh minh hoạ · Toàn mặt'), ago: 6 },
      { tag: 'Tình trạng hiện tại', note: 'Cận má phải, ánh sáng tự nhiên', url: toUrl(face({ ...base, spots }, seeded(seed)), 'Ảnh minh hoạ · Cận má', '300 240 200 200'), ago: 5 },
      portrait,
    ];
  }
  if (mang === 'PTTM') {
    return [
      { tag: 'Tình trạng hiện tại', note: 'Mắt mở tự nhiên, nhìn thẳng', url: toUrl(face({ ...base, hooded: true }, rnd), 'Ảnh minh hoạ · Cận mắt', '180 200 240 150'), ago: 5 },
      portrait,
    ];
  }
  return [portrait];
}

const seeding = new Set<number>();

/**
 * Khách mẫu chưa từng có ảnh → tạo ảnh khách gửi (lưu IndexedDB như ảnh thật) và đặt ảnh chân dung làm ảnh đại diện.
 * Chỉ chạy khi `photos` chưa có (undefined) — xoá hết ảnh thì không tạo lại.
 */
export async function ensureDemoPhotos(r: ChannelCustomerItem, l: LeadLocal, mang: Mang | undefined, patch: (id: number, p: Partial<LeadLocal>) => void) {
  if (!isDemoId(r.id) || l.photos !== undefined || seeding.has(r.id)) return;
  seeding.add(r.id);
  try {
    const sent = r.created_at ? new Date(r.created_at).valueOf() : Date.now();
    const by = r.in_charge_staff?.name || 'Trực page';
    const shots = shotsFor(mang, r.gender === 0, r.id, r.note || '');
    const photos: LeadPhoto[] = shots.map((s, i) => ({
      id: `demo${i + 1}`, kind: 'profile', tag: s.tag, takenAt: sent - s.ago * MIN, addedAt: sent + (i + 2) * MIN, by, source: 'Khách gửi', note: s.note, w: 600, h: 600,
    }));
    await Promise.all(photos.map((p, i) => putMedia(mediaKey(r.id, p.id), shots[i].url)));
    patch(r.id, { photos, avatar: photos.find((p) => p.tag === 'Chân dung')?.id });
  } catch {
    // IndexedDB bị chặn (trình duyệt ẩn danh): để trống như khách chưa có ảnh
  } finally {
    seeding.delete(r.id);
  }
}
